import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.png";

// ✅ Backend base URL (e.g. http://localhost:5000/api)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserAdminPage = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);

  // =============================
  // Fetch all admins (name, email, last_login)
  // =============================
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/auth/admins`);
      const data = res.data?.admins || [];
      setAdmins(data);
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //
  // ============================= =============================
  // Format last_login for display
  // =============================
  // Format last_login for display (force IST manually)
  // =============================
  const formatDateTime = (value) => {
    if (!value) return "Never logged in";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      // If backend sends a pre-formatted string, just show it
      return value;
    }

    // Treat the incoming date as UTC and convert to IST (UTC + 5:30)
    const utcTime =
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds()
      );

    const istOffsetMs = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes
    const istDate = new Date(utcTime + istOffsetMs);

    return istDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // =============================
  // JSX
  // =============================
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 sm:px-4 py-3">
          <img
            src={logo}
            alt="HPR Infra Logo"
            className="h-10 sm:h-12 md:h-14 w-auto object-contain"
          />
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-all text-sm sm:text-base w-full sm:w-auto"
          >
            Back to Menu
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Title + New User (Signup) */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-blue-700">
            Admin Users
          </h1>
          <button
            onClick={() => navigate("/signup")}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-all text-sm sm:text-base w-full sm:w-auto"
          >
            Add New Admin
          </button>
        </div>

        {/* MOBILE LIST */}
        <div className="md:hidden bg-white border border-gray-100 rounded-2xl shadow-md hover:shadow-lg transition-all overflow-hidden">
          {loading ? (
            <div className="text-center text-gray-500 py-8 text-sm">
              Loading admins...
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">
              No admins found.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {admins.map((admin, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] text-gray-500">Name</p>
                      <p className="font-semibold text-gray-800 text-sm mt-0.5 break-words">
                        {admin.name || "-"}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] text-gray-500">Last Login</p>
                      <p className="text-xs text-gray-700 mt-1">
                        {formatDateTime(admin.last_login)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3">
                    <p className="text-[11px] text-gray-500">Email</p>
                    <p className="text-sm text-gray-700 break-all mt-0.5">
                      {admin.email || "-"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* DESKTOP TABLE */}
        <div className="hidden md:block bg-white border border-gray-100 rounded-2xl shadow-md hover:shadow-lg transition-all overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm sm:text-base min-w-[820px]">
              <thead className="bg-green-600 text-white">
                <tr>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Name</th>
                  <th className="px-4 sm:px-6 py-3 font-semibold">Email</th>
                  <th className="px-4 sm:px-6 py-3 font-semibold text-center">
                    Last Login
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="3" className="text-center text-gray-500 py-6">
                      Loading admins...
                    </td>
                  </tr>
                )}

                {!loading &&
                  admins.map((admin, i) => (
                    <tr
                      key={i}
                      className="border-t border-gray-100 hover:bg-gray-50 transition"
                    >
                      <td className="px-4 sm:px-6 py-3 font-medium text-gray-800">
                        {admin.name || "-"}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-gray-700 break-all">
                        {admin.email}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-center text-gray-700">
                        {formatDateTime(admin.last_login)}
                      </td>
                    </tr>
                  ))}

                {!loading && admins.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center text-gray-500 py-6">
                      No admins found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserAdminPage;
